import { useEffect, useRef, useState } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { SessionKey } from '@/types/storage';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/hooks/useSidebar';
import { useDeepLink } from '@/hooks/useDeepLink';
import { useModalManager } from '@/hooks/useModalManager';
import { navigationQueueService } from '@/services/NavigationQueueService';
import {
  BreadcrumbItem,
  ContentLayout,
  DashboardTab,
  TAB_CONTENT_LAYOUT,
  TAB_SCROLL_MODE,
  TabScrollMode,
} from '@/types/dashboard';
import DashboardStats from './DashboardStats';
import MyBoard from '../MyBoard';
import UserManagement from '../UserManagement';
import OrgsManagement from '../OrgsManagement';
import People from '../People';
import ConfigManagement from '../ConfigManagement';
import BoardTemplatesManagement from '../BoardTemplatesManagement';
import LearningProgress from '../LearningProgress';
import ScheduledJobs from '../ScheduledJobs';
import JobRunDetail from '../JobRunDetail';
import SystemHealth from '../SystemHealth';
import Groups from '../Groups';
import Cohorts from '../Cohorts';
import { ScreenErrorBoundary } from '@/components/core/ScreenErrorBoundary';
import { OfflineBanner } from '@/components/core/OfflineBanner';
import { AppLayout } from '@/components/core/Layout';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { useScreenState } from '@/hooks/useScreenState';
import { ScreenType } from '@/types/screen.types';
import { logger } from '@/utils/logger';
import {
  DeepLink,
  DeepLinkEntity,
  cleanDeepLinkUrl,
  setNavigationUrl,
  clearNavigationUrl,
} from '@/utils/deep-linking';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useToast } from '@/hooks/useToast';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: screen-level navigation orchestration (GET_TASK/GET_ALL_BOARDS for routing, not user mutations)
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import type { GetTaskInput } from '@/types/generated';
import type {
  GetAllBoardsInput,
  GetAllBoardsOutput,
} from '@/types/board-discovery.types';
import type { GetCardUIOutput } from '@/types/action-outputs';
import { WeekInfo, getCurrentWeek, getUSWeek, getWeekInfo } from '@/utils/week';
import { AsyncButton } from '@/components/ui';
import { ShareButton } from '@/components/core/ShareButton';
import { extractTimeFromUUIDv7, isUUIDv7 } from '@/utils/uuidv7';
import { BoardSettingsModal } from '@/components/board';
import { BoardPreferences } from '@/types/preferences.types';
import { BackgroundDisplayMode } from '@/types/BackgroundDisplayMode';
import { useBoardPreferencesQuery } from '@/hooks/queries';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';

type GetTaskOutput = GetCardUIOutput;

interface DashboardProps {
  className?: string;
}

export default function Dashboard({ className = '' }: DashboardProps) {
  const { user } = useAuth();
  const { activeTab, tabs, setActiveTab } = useSidebar();
  const { pendingDeepLink, setPendingDeepLink } = useDeepLink();
  const { getStack } = useModalManager();
  const toast = useToast();

  // boardIdForKanban: the prop fed into <MyBoard boardId={...}> — only changed
  // on explicit board switches (user picker, deep links) to avoid cascade reloads.
  const [boardIdForKanban, setBoardIdForKanban] = useSessionState<
    string | undefined
  >(SessionKey.LB_DASHBOARD_BOARD_ID, undefined);
  // currentBoardId: UI state only (header, settings modal, board selector).
  // Updated by handleBoardLoad after each successful load — does NOT cause KanbanBoard to reload.
  const [currentBoardId, setCurrentBoardId] = useState<string | undefined>(
    undefined
  );
  const [currentBoardName, setCurrentBoardName] = useState<string | undefined>(
    undefined
  );
  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [isGroupBoard, setIsGroupBoard] = useState(false);
  const [boardOwnerId, setBoardOwnerId] = useState<string | undefined>(
    undefined
  );
  const [boardTemplateId, setBoardTemplateId] = useState<string | undefined>(
    undefined
  );
  const [initialCardId, setInitialCardId] = useState<string | undefined>(
    undefined
  );
  const [selectedWeekKey, setSelectedWeekKey] = useSessionState<
    { week: number; year: number } | undefined
  >(SessionKey.LB_DASHBOARD_SELECTED_WEEK, undefined);
  const selectedWeek: WeekInfo = selectedWeekKey
    ? getWeekInfo(selectedWeekKey.week, selectedWeekKey.year)
    : getCurrentWeek();
  const setSelectedWeek = (week: WeekInfo) =>
    setSelectedWeekKey({ week: week.week, year: week.year });
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [peopleSelectedUserId, setPeopleSelectedUserId] = useSessionState<
    string | null
  >(SessionKey.LB_PEOPLE_SELECTED_USER_ID, null);
  const [peopleSelectedUserName, setPeopleSelectedUserName] = useState<
    string | null
  >(null);
  const [deepLinkRunId, setHighlightRunId] = useState<string | undefined>(
    undefined
  );

  // Group Assignments header state (sync lifted to header)
  const [gaGroupName, setGaGroupName] = useState<string | null>(null);
  const [gaIsSyncing, setGaIsSyncing] = useState(false);
  const gaSyncFnRef = useRef<(() => Promise<void>) | null>(null);
  const [gaCohortsBoardId, setGaCohortsBoardId] = useState<string | null>(null);
  // Board ID sourced from a COHORT_BOARD deep link — bypasses group selection
  const [cohortDeepLinkBoardId, setCohortDeepLinkBoardId] = useState<
    string | null
  >(null);
  // Browser back/forward navigation support
  const { navigateToTab, navigateToSubView } = useNavigationHistory({
    activeTab,
    setActiveTab,
    peopleSelectedUserId,
    setPeopleSelectedUserId,
  });

  // TQ query for board preferences — re-fetches automatically when currentBoardId changes
  const { data: boardPreferencesData } =
    useBoardPreferencesQuery(currentBoardId);

  // Map raw query data to BoardPreferences shape
  const boardPreferences: BoardPreferences | undefined = boardPreferencesData
    ? {
        background: boardPreferencesData.background
          ? {
              absolute_url: boardPreferencesData.background.absolute_url,
              display_mode: boardPreferencesData.background.display_mode as
                | BackgroundDisplayMode
                | undefined,
            }
          : undefined,
      }
    : undefined;

  /**
   * Handle board preferences change with logging (from BoardSettingsModal)
   * Invalidates TQ cache so the fresh preferences are fetched from the server.
   */
  const handleBoardPreferencesChange = (prefs: BoardPreferences) => {
    logger.debug('[Dashboard] Board preferences changed:', { prefs });
    logger.debug('[Dashboard] Background display mode:', {
      display_mode: prefs.background?.display_mode,
    });
    // Cache invalidation handled by UPDATE_BOARD_BACKGROUND side effect
  };

  /**
   * Navigate to a card by ID
   * Flow: GET_CARD → switch tab → set board → set initialCardId
   */
  const navigateToCard = async (taskId: string) => {
    try {
      logger.info('[Dashboard] Navigating to card', { task_id: taskId });

      // Fetch card to get board_id
      const result = await actionOrchestrator.execute<
        GetTaskInput,
        GetTaskOutput
      >(Action.GET_TASK, { task_id: taskId });

      if (result.code !== ResponseCode.SUCCESS || !result.data) {
        toast.error('Card not found or access denied');
        cleanDeepLinkUrl();
        return;
      }

      const { card } = result.data;

      // Switch to my-board tab
      setActiveTab(DashboardTab.MY_BOARD);

      // Set the board input for KanbanBoard (triggers the load)
      setBoardIdForKanban(card.board_id);

      // Calculate week from card's dates to maintain weekly context
      // Priority: start_date (user can update) → UUID v7 (immutable fallback) → created_at → current week
      //
      // IMPORTANT: start_date is source of truth because:
      // - User can reschedule cards to different weeks
      // - UUID v7 timestamp is immutable (set at creation)
      // - If start_date exists, always use it (even if UUID v7 differs)
      let cardWeek: WeekInfo;
      let dateSource: string;

      if (card.start_date) {
        // Use start_date as source of truth (handles rescheduled cards)
        cardWeek = getUSWeek(new Date(card.start_date));
        dateSource = 'start_date';

        // Detect if card was rescheduled (UUID v7 differs from start_date)
        if (isUUIDv7(taskId)) {
          const uuidTime = extractTimeFromUUIDv7(taskId);
          if (uuidTime) {
            const uuidWeek = getUSWeek(uuidTime);
            if (
              uuidWeek.week !== cardWeek.week ||
              uuidWeek.year !== cardWeek.year
            ) {
              logger.info('[Dashboard] Card was rescheduled', {
                task_id: taskId,
                uuid_week: `${uuidWeek.year}-W${uuidWeek.week}`,
                current_week: `${cardWeek.year}-W${cardWeek.week}`,
              });
            }
          }
        }
      } else if (isUUIDv7(taskId)) {
        // Fallback: Extract timestamp from UUID v7 (only if start_date missing)
        const uuidTime = extractTimeFromUUIDv7(taskId);
        if (uuidTime) {
          cardWeek = getUSWeek(uuidTime);
          dateSource = 'uuid_v7_fallback';
        } else {
          cardWeek = card.created_at
            ? getUSWeek(new Date(card.created_at))
            : getCurrentWeek();
          dateSource = card.created_at ? 'created_at' : 'current_week';
        }
      } else if (card.created_at) {
        cardWeek = getUSWeek(new Date(card.created_at));
        dateSource = 'created_at';
      } else {
        cardWeek = getCurrentWeek();
        dateSource = 'current_week';
      }
      setSelectedWeek(cardWeek);

      // Set initialCardId to trigger auto-open in KanbanBoard
      setInitialCardId(taskId);

      logger.info('[Dashboard] Card navigation setup complete', {
        task_id: taskId,
        board_id: card.board_id,
        week: cardWeek.week,
        year: cardWeek.year,
        date_source: dateSource,
        is_uuid_v7: isUUIDv7(taskId),
      });
    } catch (error) {
      logger.error('[Dashboard] Error navigating to card', {
        error,
        task_id: taskId,
      });
      toast.error('Failed to navigate to card');
      cleanDeepLinkUrl();
    }
  };

  /**
   * Navigate to a board by ID
   * Flow: set board → switch tab (to prevent flickering)
   * URL cleanup is handled by MyBoard's onBoardLoad callback
   */
  const navigateToBoard = async (boardId: string, deepLink?: DeepLink) => {
    try {
      logger.info('[Dashboard] Navigating to board', { board_id: boardId });

      // IMPORTANT: Set board ID BEFORE switching tabs to prevent flickering
      // If we switch tabs first, MyBoard briefly loads with undefined boardId (user's own board)
      setBoardIdForKanban(boardId);

      // Use week from deep link query params if present, otherwise fall back to current week
      const weekParam = deepLink?.queryParams?.week;
      const yearParam = deepLink?.queryParams?.year;
      const week =
        weekParam && yearParam
          ? getWeekInfo(Number(weekParam), Number(yearParam))
          : getCurrentWeek();
      setSelectedWeek(week);

      // Then switch to my-board tab — use navigateToTab so a history entry is pushed,
      // enabling browser back to return to the profile view
      navigateToTab(DashboardTab.MY_BOARD);

      logger.info('[Dashboard] Board navigation setup complete', {
        board_id: boardId,
      });

      // URL cleanup will be handled by MyBoard's onBoardLoad callback
    } catch (error) {
      logger.error('[Dashboard] Error navigating to board', {
        error,
        board_id: boardId,
      });
      toast.error('Failed to navigate to board');
      clearNavigationUrl();
    }
  };

  /**
   * Navigate to a user profile by ID
   * Flow: switch to people tab → set selectedUserId
   */
  const navigateToProfile = (userId: string) => {
    logger.info('[Dashboard] Navigating to user profile', { user_id: userId });
    setActiveTab(DashboardTab.PEOPLE);
    setPeopleSelectedUserId(userId);
    setNavigationUrl(DeepLinkEntity.USER, userId);
    logger.info('[Dashboard] Profile navigation setup complete', {
      user_id: userId,
    });
  };

  /**
   * Navigate directly to a job run by run ID (no job ID needed)
   */
  const navigateToJobRun = (runId: string) => {
    logger.info('[Dashboard] Navigating to job run', { run_id: runId });
    setHighlightRunId(runId);
    setActiveTab(DashboardTab.JOB_RUN_DETAIL);
    setNavigationUrl(DeepLinkEntity.JOB_RUN, runId);
  };

  /**
   * Handle deep link navigation based on entity type
   */
  const handleDeepLinkNavigation = async (deepLink: DeepLink) => {
    logger.info('[Dashboard] Handling deep link navigation', { deepLink });

    switch (deepLink.entity) {
      case DeepLinkEntity.CARD:
        await navigateToCard(deepLink.id);
        break;
      case DeepLinkEntity.BOARD:
        await navigateToBoard(deepLink.id, deepLink);
        break;
      case DeepLinkEntity.USER:
        navigateToProfile(deepLink.id);
        break;
      case DeepLinkEntity.JOB_RUN:
        navigateToJobRun(deepLink.id);
        break;
      case DeepLinkEntity.COHORT_BOARD:
        setCohortDeepLinkBoardId(deepLink.id);
        setActiveTab(DashboardTab.COHORTS);
        setNavigationUrl(DeepLinkEntity.COHORT_BOARD, deepLink.id);
        break;
      default:
        logger.warn('[Dashboard] Unknown deep link entity', { deepLink });
        toast.error('Unknown link type');
        cleanDeepLinkUrl();
    }

    // Clear pending deep link after handling
    setPendingDeepLink(null);
  };

  /**
   * Process pending deep links on mount or when pendingDeepLink changes
   */
  useEffect(() => {
    if (pendingDeepLink) {
      handleDeepLinkNavigation(pendingDeepLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeepLink]);

  /**
   * Check for queued navigation when modals close
   * When modal stack is empty and navigation queue has items, execute them
   */
  useEffect(() => {
    const stack = getStack();

    // Only process queue when no modals are open
    if (stack.length === 0 && navigationQueueService.hasPending()) {
      const actions = navigationQueueService.flush();

      logger.info('[Dashboard] Processing queued navigation actions', {
        count: actions.length,
      });

      // Execute all queued actions
      actions.forEach(action => {
        const deepLink: DeepLink = {
          entity: action.type as DeepLinkEntity,
          id: action.id,
          queryParams: action.params as Record<string, string> | undefined,
        };

        handleDeepLinkNavigation(deepLink);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStack()]);

  // Screen state management with required load() implementation
  useScreenState({
    metadata: {
      screenType: ScreenType.DASHBOARD,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, screenState) => {
      // Dashboard doesn't need to load data - it's just a shell
      // Child components (like KanbanBoard) handle their own data loading
      logger.info('Dashboard loading', { user: user?.email });
      screenState.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Dashboard unmounted');
      },
    },
  });

  // Context-aware breadcrumbs - show current page name, clickable to reset to tab's first page
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    if (activeTab === DashboardTab.JOB_RUN_DETAIL) {
      return [
        {
          label: 'Jobs',
          isActive: false,
          onClick: () => setActiveTab(DashboardTab.SCHEDULED_JOBS),
        },
        { label: 'Job Run Details', isActive: true },
      ];
    }

    const activeTabName =
      tabs.find(t => t.id === activeTab)?.name || 'Dashboard';

    const handleTabBreadcrumbClick = () => {
      // Reset tab to its first/default page
      if (activeTab === DashboardTab.PEOPLE) {
        navigateToSubView({ selectedUserId: null });
      }
    };

    // If viewing a sub-page within a tab, show tab name as clickable + sub-page as active
    if (activeTab === DashboardTab.PEOPLE && peopleSelectedUserId) {
      return [
        {
          label: activeTabName,
          isActive: false,
          onClick: handleTabBreadcrumbClick,
        },
        { label: peopleSelectedUserName ?? 'Profile', isActive: true },
      ];
    }

    if (activeTab === DashboardTab.COHORTS && gaGroupName) {
      return [
        {
          label: activeTabName,
          isActive: false,
          onClick: handleTabBreadcrumbClick,
        },
        { label: gaGroupName, isActive: true },
      ];
    }

    return [
      {
        label: activeTabName,
        isActive: true,
        onClick: handleTabBreadcrumbClick,
      },
    ];
  };

  const getContentLayout = (): ContentLayout => {
    // People tab switches to NARROW when viewing a profile
    if (activeTab === DashboardTab.PEOPLE && peopleSelectedUserId) {
      return ContentLayout.NARROW;
    }
    return TAB_CONTENT_LAYOUT[activeTab as DashboardTab] ?? ContentLayout.FULL;
  };

  const getScrollMode = (): TabScrollMode =>
    TAB_SCROLL_MODE[activeTab as DashboardTab] ?? TabScrollMode.PAGE;

  const breadcrumbs = getBreadcrumbs();

  // Handle board load callback to update header and clean URL
  const handleBoardLoad = (
    boardId: string,
    boardName: string,
    canEdit: boolean,
    groupBoard: boolean,
    ownerId: string | undefined,
    templateId: string | undefined
  ) => {
    setCurrentBoardId(boardId);
    setCurrentBoardName(boardName);
    setHasEditPermission(canEdit);
    setIsGroupBoard(groupBoard);
    setBoardOwnerId(ownerId);
    setBoardTemplateId(templateId);

    // Stamp URL with current board + week after board loads successfully
    const week = selectedWeek ?? getCurrentWeek();
    setNavigationUrl(DeepLinkEntity.BOARD, boardId, {
      week: String(week.week),
      year: String(week.year),
    });
    // Board preferences are fetched automatically via useBoardPreferencesQuery
    // when currentBoardId changes above
  };

  // Handle board change from selector
  const handleBoardChange = (boardId: string | undefined) => {
    setBoardIdForKanban(boardId); // Triggers KanbanBoard to load the new board
    setCurrentBoardId(boardId); // Keep UI selector in sync immediately
    setSelectedWeek(getCurrentWeek()); // Reset to current week
    setHasEditPermission(false); // Reset permission until new board loads
    // Board name and permissions will be updated when MyBoard loads the new board
  };

  const handleBoardDeleted = async () => {
    const deletedBoardId = currentBoardId;
    setIsSettingsModalOpen(false);
    setCurrentBoardId(undefined);
    setCurrentBoardName(undefined);
    setBoardIdForKanban(undefined);
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });

    if (!user?.id) return;

    const result = await actionOrchestrator.execute<
      GetAllBoardsInput,
      GetAllBoardsOutput
    >(Action.GET_ALL_BOARDS, { user_id: user.id } as GetAllBoardsInput);

    if (result.code === ResponseCode.SUCCESS && result.data) {
      const { boards } = result.data;
      const nextBoard = [
        ...boards.my_boards,
        ...boards.group_boards,
        ...boards.same_role_boards,
        ...boards.other_boards,
      ].find(b => b.id !== deletedBoardId);

      if (nextBoard) {
        handleBoardChange(nextBoard.id);
      }
    }
  };

  // Handle when initial card modal opens (for deep link navigation)
  const handleInitialCardOpened = () => {
    logger.info('[Dashboard] Initial card opened, cleaning URL');
    cleanDeepLinkUrl();
    setInitialCardId(undefined); // Clear the initial card ID
  };

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case DashboardTab.DASHBOARD:
        return <DashboardStats />;
      case DashboardTab.MY_BOARD:
        return (
          <MyBoard
            boardId={boardIdForKanban}
            onBoardLoad={handleBoardLoad}
            initialCardId={initialCardId}
            onInitialCardOpened={handleInitialCardOpened}
            selectedWeek={selectedWeek}
            boardBackground={boardPreferences?.background}
          />
        );
      case DashboardTab.PEOPLE:
        return (
          <People
            selectedUserId={peopleSelectedUserId}
            onSelectedUserIdChange={id => {
              if (!id) setPeopleSelectedUserName(null);
              navigateToSubView({ selectedUserId: id });
            }}
            onBoardClick={boardId => navigateToBoard(boardId)}
            onProfileLoad={displayName =>
              setPeopleSelectedUserName(displayName)
            }
          />
        );
      case DashboardTab.MANAGE_PEOPLE:
        return <UserManagement />;
      case DashboardTab.ORGS_MANAGEMENT:
        return <OrgsManagement />;
      case DashboardTab.CONFIG_MANAGEMENT:
        return <ConfigManagement />;
      case DashboardTab.BOARD_TEMPLATES:
        return <BoardTemplatesManagement />;
      case DashboardTab.LEARNING_PROGRESS:
        return <LearningProgress />;
      case DashboardTab.SCHEDULED_JOBS:
        return <ScheduledJobs />;
      case DashboardTab.JOB_RUN_DETAIL:
        return deepLinkRunId ? <JobRunDetail runId={deepLinkRunId} /> : null;
      case DashboardTab.SYSTEM_HEALTH:
        return <SystemHealth />;
      case DashboardTab.GROUPS:
        return <Groups />;
      case DashboardTab.COHORTS:
        return (
          <Cohorts
            selectedWeek={selectedWeek ?? getCurrentWeek()}
            onGroupChange={group => setGaGroupName(group?.name ?? null)}
            onSyncStateChange={(syncFn, isSyncing) => {
              gaSyncFnRef.current = syncFn;
              setGaIsSyncing(isSyncing);
            }}
            onBoardIdChange={boardId => setGaCohortsBoardId(boardId)}
            initialBoardId={cohortDeepLinkBoardId ?? undefined}
          />
        );
      default:
        return <DashboardStats />;
    }
  };

  if (!user) {
    return null; // Should not render if user is not authenticated
  }

  return (
    <ScreenErrorBoundary>
      <OfflineBanner />
      <AppLayout
        className={className}
        contentLayout={getContentLayout()}
        scrollMode={getScrollMode()}
        breadcrumbs={breadcrumbs}
        activeTab={activeTab}
        onTabChange={(tab: string) => {
          navigateToTab(tab);
          if (tab !== DashboardTab.MY_BOARD) {
            clearNavigationUrl();
          }
          // Clear cohort deep-link when navigating away so group selection resumes normally
          if (tab !== DashboardTab.COHORTS) {
            setCohortDeepLinkBoardId(null);
          }
          // MY_BOARD: handleBoardLoad fires on mount and stamps URL
        }}
        currentBoardId={
          activeTab === DashboardTab.MY_BOARD ? currentBoardId : undefined
        }
        currentBoardName={
          activeTab === DashboardTab.MY_BOARD ? currentBoardName : undefined
        }
        onBoardChange={
          activeTab === DashboardTab.MY_BOARD ? handleBoardChange : undefined
        }
        showWeekNavigator={
          activeTab === DashboardTab.MY_BOARD ||
          activeTab === DashboardTab.COHORTS
        }
        selectedWeek={selectedWeek}
        onWeekChange={(week: WeekInfo) => {
          setSelectedWeek(week);
          if (currentBoardId) {
            setNavigationUrl(DeepLinkEntity.BOARD, currentBoardId, {
              week: String(week.week),
              year: String(week.year),
            });
          }
        }}
        showBoardSettings={
          activeTab === DashboardTab.MY_BOARD &&
          hasEditPermission &&
          !!currentBoardId
        }
        onBoardSettingsClick={() => setIsSettingsModalOpen(true)}
        headerExtras={
          activeTab === DashboardTab.COHORTS ? (
            <CohortsHeaderExtras
              isSyncing={gaIsSyncing}
              hasSyncFn={gaSyncFnRef.current !== null}
              onSync={async () => {
                await gaSyncFnRef.current?.();
              }}
              boardId={gaCohortsBoardId}
              selectedWeek={selectedWeek}
            />
          ) : undefined
        }
        boardBackground={boardPreferences?.background}
      >
        {renderActiveTabContent()}
      </AppLayout>

      {/* Board Settings Modal */}
      {currentBoardId && (
        <BoardSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          boardId={currentBoardId}
          currentPreferences={boardPreferences}
          onPreferencesChange={handleBoardPreferencesChange}
          boardTitle={currentBoardName}
          isGroupBoard={isGroupBoard}
          ownerId={boardOwnerId}
          templateId={boardTemplateId}
          hasEditPermission={hasEditPermission}
          isAdmin={user?.organization?.is_superadmin_org === true}
          onBoardDeleted={handleBoardDeleted}
        />
      )}
    </ScreenErrorBoundary>
  );
}

// Header controls rendered in AppHeader for Cohorts tab
function CohortsHeaderExtras({
  isSyncing,
  hasSyncFn,
  onSync,
  boardId,
  selectedWeek,
}: {
  isSyncing: boolean;
  hasSyncFn: boolean;
  onSync: () => Promise<void>;
  boardId: string | null;
  selectedWeek: WeekInfo | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      {boardId && (
        <ShareButton
          entity={DeepLinkEntity.COHORT_BOARD}
          id={boardId}
          ariaLabel="Copy board link"
          queryParams={
            selectedWeek
              ? {
                  week: String(selectedWeek.week),
                  year: String(selectedWeek.year),
                }
              : undefined
          }
        />
      )}
      {hasSyncFn && (
        <AsyncButton
          isLoading={isSyncing}
          onClick={onSync}
          variant="secondary"
          size="sm"
        >
          Sync Cards
        </AsyncButton>
      )}
    </div>
  );
}
